import { ChevronDownIcon, ClockIcon } from "@heroicons/react/24/outline"
import { XMarkIcon } from "@heroicons/react/24/solid"
import React, { useCallback, useEffect, useRef, useState } from "react"

import HealmeAiMark from "../components/icons/HealmeAiMark"
import {
  dispatchPortalAiRefreshSurfaces,
  PORTAL_AI_INTAKE_FORM_GENERATION_PROMPT
} from "../constants/portalAiAssistantEvents"

import ImagePickerV2Js from "./WebsiteBuilderV2/components/shared/ImagePickerV2"
import DocumentPickerV2, {
  type DocumentPickerSelection,
  type UploadedPortalDocument
} from "./WebsiteBuilderV2/components/shared/DocumentPickerV2"
import PortalAiAssistantChatMarkdown from "./PortalAiAssistantChatMarkdown"
import PortalAiAssistantHistoryDrawer from "./PortalAiAssistantHistoryDrawer"
import {
  clearLastConversationId,
  readLastConversationId,
  writeLastConversationId
} from "../utils/portalAiAssistantStorage"

type ChatRole = "user" | "assistant"
type ChatAttachmentKind = "image" | "document"

type ChatMessage = {
  id?: number | string
  role: ChatRole
  content: string
  createdAt?: string
  attachments?: ChatAttachment[]
  websiteReference?: WebsiteReference | null
}

type ChatAttachment = {
  url: string
  kind?: ChatAttachmentKind
  alt?: string | null
  filename?: string | null
  contentType?: string | null
  provider?: string | null
  handle?: string | null
}

type WebsiteReference = {
  url: string
  excerpt?: string
  excerptLength?: number
}

export type PortalAiAssistantChatPanelProps = {
  chatUrl: string
  chatStatusUrl: string
  conversationUrl: string
  conversationsUrl: string
  userId?: number
  practiceId?: number
  practiceName?: string
  practiceData?: Record<string, unknown>
  /** When false (fab closed), skips loading until opened */
  active: boolean
  /** Prefill composer when opened via PORTAL_AI_ASSISTANT_OPEN_REQUEST */
  initialPrompt?: string | null
  onInitialPromptApplied?: () => void
  /** Intake Forms page: which form to update (sent to API, not shown in the message). */
  initialIntakeFormId?: number | null
  onInitialIntakeFormContextApplied?: () => void
  onClose?: () => void
}

type ConversationJson = {
  conversationId: number | null
  messages: Array<{
    id: number
    role: ChatRole
    content: string
    createdAt: string
    attachments?: ChatAttachment[]
    websiteReference?: WebsiteReference | null
  }>
}

/** Must match `Portal::AiAssistant::FixedReplies::CAPABILITIES_USER_PROMPT` (fixed server reply, no model). */
export const PORTAL_AI_CAPABILITIES_PROMPT = "What can you do in my portal? (full list)"

const CHAT_TURN_POLL_INTERVAL_MS = 1500
const CHAT_TURN_POLL_SLOW_INTERVAL_MS = 5000
/** After this, show a "still working" message but keep polling until max. */
const CHAT_TURN_POLL_EXTENDED_WAIT_MS = 150_000
/** Align with server stale window: RUNNING_STALE_AFTER plus pending queue time. */
const CHAT_TURN_POLL_MAX_MS = (30 + 2) * 60 * 1000 + 5000

type ChatTurnStatusJson = {
  turn_id?: number
  conversation_id?: number
  status?: string
  reply?: string
  error?: string
  message?: string
  detail?: string
  refresh_builder_preview?: boolean
  refresh_surfaces?: string[]
}

async function sleep(ms: number): Promise<void> {
  await new Promise((resolve) => {
    window.setTimeout(resolve, ms)
  })
}

async function pollChatTurnStatus(
  chatStatusUrl: string,
  turnId: number,
  options?: {
    signal?: AbortSignal
    onExtendedWait?: () => void
    onPoll?: (data: ChatTurnStatusJson) => void
  }
): Promise<{ ok: true; data: ChatTurnStatusJson } | { ok: false; data: ChatTurnStatusJson; httpStatus?: number }> {
  const started = Date.now()
  let extendedWaitNotified = false

  while (Date.now() - started < CHAT_TURN_POLL_MAX_MS) {
    const signal = options?.signal
    if (signal?.aborted) {
      return { ok: false, data: { status: "aborted", message: "Request cancelled." } }
    }

    const res = await fetch(`${chatStatusUrl}?turn_id=${encodeURIComponent(String(turnId))}`, {
      headers: { Accept: "application/json" },
      signal
    })

    let data: ChatTurnStatusJson = {}
    try {
      data = (await res.json()) as ChatTurnStatusJson
    } catch {
      data = {}
    }

    if (!res.ok) {
      return { ok: false, data, httpStatus: res.status }
    }

    options?.onPoll?.(data)

    if (data.status === "completed" || data.status === "failed") {
      return { ok: data.status === "completed", data }
    }

    const elapsed = Date.now() - started
    if (!extendedWaitNotified && elapsed >= CHAT_TURN_POLL_EXTENDED_WAIT_MS) {
      extendedWaitNotified = true
      options?.onExtendedWait?.()
    }

    const intervalMs =
      elapsed >= CHAT_TURN_POLL_EXTENDED_WAIT_MS ? CHAT_TURN_POLL_SLOW_INTERVAL_MS : CHAT_TURN_POLL_INTERVAL_MS
    await sleep(intervalMs)
  }

  return {
    ok: false,
    data: {
      status: "timeout",
      error: "timeout",
      message: "The assistant timed out before finishing. Try sending your message again."
    }
  }
}

function parseUploadedDocuments(practiceData?: Record<string, unknown>): UploadedPortalDocument[] {
  const raw = practiceData?.uploadedDocuments
  if (!Array.isArray(raw)) return []
  return raw
    .map((item) => {
      if (!item || typeof item !== "object") return null
      const doc = item as Record<string, unknown>
      const id = doc.id
      const url = String(doc.url || "")
      if (id == null || !url) return null
      return {
        id: id as string | number,
        url,
        filename: String(doc.filename || "document"),
        handle: String(doc.handle || ""),
        contentType: String(doc.contentType || doc.content_type || ""),
        provider: String(doc.provider || "filestack")
      }
    })
    .filter((d): d is UploadedPortalDocument => d != null)
}

/**
 * Spinning-wheel "working" indicator.
 *
 * N dots are placed around a circle of radius `WORKING_WHEEL_RADIUS_PX` by giving
 * each an individual positioning `transform: rotate(angle) translateY(-radius)`.
 * All dots share the same `animate-portal-ai-dot-spin` keyframe (opacity-only)
 * with a staggered `animation-delay` that is a fraction of the cycle. The visual
 * result is a single bright spot that travels clockwise around the wheel — the
 * classic "rueda girando" spinner, using positive start times as requested.
 */
const WORKING_DOT_COUNT = 8
const WORKING_DOT_CYCLE_MS = 1000
const WORKING_DOT_STAGGER_MS = WORKING_DOT_CYCLE_MS / WORKING_DOT_COUNT
const WORKING_WHEEL_RADIUS_PX = 6
const WORKING_DOT_MOTION: Array<React.CSSProperties> = Array.from(
  { length: WORKING_DOT_COUNT },
  (_, i) => ({
    transform: `rotate(${(360 / WORKING_DOT_COUNT) * i}deg) translateY(-${WORKING_WHEEL_RADIUS_PX}px)`,
    animationDelay: `${Math.round(WORKING_DOT_STAGGER_MS * i)}ms`
  })
)

const SUGGESTED_PROMPTS = [
  PORTAL_AI_CAPABILITIES_PROMPT,
  "What is on my calendar in the next 7 days?",
  "Summarize my public website profile (headline, about, services count).",
  "What Builder pages do I have listed, and what is each page about?",
  "List my next 10 upcoming appointments.",
  "What preset themes are available for my Builder site, and which theme am I using now?",
  "Review my homepage SEO (title, meta description, key headings) and suggest concrete improvements.",
  "List my services with any prices or durations you can see, and flag anything that looks incomplete.",
  "Show me the main sections on my homepage and whether anything looks out of date or empty.",
  "What should I verify before I publish my site, and what does “published” mean for my practice on Heal.me?",
  "Can you draft a short paragraph for my About section that matches my current services and tone? Do not publish — just propose text.",
  PORTAL_AI_INTAKE_FORM_GENERATION_PROMPT
]

function readCsrfToken(): string {
  if (typeof document === "undefined") return ""
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") ?? ""
}

type AssistantChatError = {
  message: string
  detail: string | null
  code?: string | null
}

function parseAssistantErrorBody(data: unknown, httpStatus?: number): AssistantChatError {
  const body = data && typeof data === "object" ? (data as Record<string, unknown>) : {}
  const code = typeof body.error === "string" ? body.error : null
  const message =
    (typeof body.message === "string" && body.message) ||
    code ||
    (httpStatus ? `Request failed (HTTP ${httpStatus})` : "Request failed")
  const detail =
    (typeof body.detail === "string" && body.detail) ||
    (httpStatus && httpStatus >= 400 ? `HTTP ${httpStatus}` : null)

  return { message, detail, code }
}

function PortalAiAssistantErrorBanner({
  error,
  showDetails,
  onToggleDetails
}: {
  error: AssistantChatError
  showDetails: boolean
  onToggleDetails: () => void
}) {
  const hasDetail = Boolean(error.detail?.trim())

  return (
    <div className="shrink-0 border-t border-gray-200 bg-red-50 px-4 py-2" role="alert">
      <p className="text-xs text-red-800">{error.message}</p>
      {hasDetail ? (
        <div className="mt-1">
          <button
            type="button"
            className="text-xs font-medium text-red-900 underline decoration-red-400 underline-offset-2 hover:text-red-950"
            aria-expanded={showDetails}
            onClick={onToggleDetails}>
            {showDetails ? "Hide details" : "Details"}
          </button>
          {showDetails ? (
            <pre className="mt-1.5 max-h-36 overflow-auto whitespace-pre-wrap break-words rounded-md border border-red-200 bg-white/80 p-2 font-mono text-[10px] leading-snug text-red-950">
              {error.code ? `[${error.code}]\n` : ""}
              {error.detail}
            </pre>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

function attachmentKind(attachment: ChatAttachment): ChatAttachmentKind {
  return attachment.kind === "document" ? "document" : "image"
}

function attachmentLabel(attachment: ChatAttachment): string {
  if (attachment.filename) return attachment.filename
  if (attachment.alt) return attachment.alt
  try {
    return decodeURIComponent(new URL(attachment.url).pathname.split("/").pop() || "Document")
  } catch {
    return "Document"
  }
}

function ChatAttachmentPreview({
  attachment,
  variant
}: {
  attachment: ChatAttachment
  variant: "user" | "assistant" | "pending"
}) {
  const isDocument = attachmentKind(attachment) === "document"
  const label = attachmentLabel(attachment)

  if (isDocument) {
    const chipClass =
      variant === "user"
        ? "border-teal-400/40 bg-teal-700/40 text-teal-50"
        : "border-gray-200 bg-gray-50 text-gray-700"
    return (
      <a
        href={attachment.url}
        target="_blank"
        rel="noreferrer"
        className={`inline-flex max-w-[12rem] items-center gap-1.5 truncate rounded-md border px-2 py-1 text-[11px] ${chipClass}`}
        title={label}>
        <svg className="size-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
        <span className="truncate">{label}</span>
      </a>
    )
  }

  return (
    <a href={attachment.url} target="_blank" rel="noreferrer">
      <img
        src={attachment.url}
        alt={attachment.alt || label}
        className={variant === "pending" ? "h-14 w-14 rounded-md object-cover" : "h-16 w-16 rounded-md object-cover"}
      />
    </a>
  )
}

function formatShortTime(iso?: string): string {
  if (!iso) return ""
  try {
    const d = new Date(iso)
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
  } catch {
    return ""
  }
}

export default function PortalAiAssistantChatPanel({
  chatUrl,
  chatStatusUrl,
  conversationUrl,
  conversationsUrl,
  userId,
  practiceId,
  practiceName,
  practiceData,
  active,
  initialPrompt = null,
  onInitialPromptApplied,
  initialIntakeFormId = null,
  onInitialIntakeFormContextApplied,
  onClose
}: PortalAiAssistantChatPanelProps) {
  const ImagePickerV2 = ImagePickerV2Js as unknown as React.ComponentType<Record<string, unknown>>
  const intakeFormIdForNextSendRef = useRef<number | null>(null)
  const [conversationId, setConversationId] = useState<number | null>(null)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [initialLoad, setInitialLoad] = useState(true)
  const [draft, setDraft] = useState("")
  const [loading, setLoading] = useState(false)
  const [chatError, setChatError] = useState<AssistantChatError | null>(null)
  const [showErrorDetails, setShowErrorDetails] = useState(false)

  const setError = useCallback((message: string, detail?: string | null, code?: string | null) => {
    setChatError({ message, detail: detail?.trim() || null, code: code ?? null })
    setShowErrorDetails(false)
  }, [])

  const clearError = useCallback(() => {
    setChatError(null)
    setShowErrorDetails(false)
  }, [])
  const [pendingAttachments, setPendingAttachments] = useState<ChatAttachment[]>([])
  const [uploadedDocuments, setUploadedDocuments] = useState<UploadedPortalDocument[]>(() =>
    parseUploadedDocuments(practiceData)
  )
  const [documentPickerError, setDocumentPickerError] = useState<string | null>(null)
  const [attachMenuOpen, setAttachMenuOpen] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [pendingWebsiteReference, setPendingWebsiteReference] = useState<string | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const imagePickerTriggerRef = useRef<HTMLButtonElement>(null)
  const documentPickerTriggerRef = useRef<HTMLButtonElement>(null)
  const attachMenuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setUploadedDocuments(parseUploadedDocuments(practiceData))
  }, [practiceData])

  const persistLastConversation = useCallback(
    (id: number | null) => {
      if (userId == null || practiceId == null) return
      writeLastConversationId(userId, practiceId, id)
    },
    [practiceId, userId]
  )

  const loadConversation = useCallback(
    async (id?: number | null): Promise<{ ok: boolean; notFound?: boolean }> => {
      const query = id != null ? `?conversation_id=${encodeURIComponent(String(id))}` : ""
      const res = await fetch(`${conversationUrl}${query}`, {
        headers: { Accept: "application/json" }
      })
      if (res.status === 404) {
        return { ok: false, notFound: true }
      }
      if (!res.ok) {
        setError("Could not load conversation.", `HTTP ${res.status}`)
        return { ok: false }
      }
      const data = (await res.json()) as ConversationJson
      setConversationId(data.conversationId)
      setMessages(
        data.messages.map((m) => ({
          id: m.id,
          role: m.role,
          content: m.content,
          createdAt: m.createdAt,
          attachments: m.attachments || [],
          websiteReference: m.websiteReference || null
        }))
      )
      if (data.conversationId != null) {
        persistLastConversation(data.conversationId)
      }
      return { ok: true }
    },
    [conversationUrl, persistLastConversation, setError]
  )

  useEffect(() => {
    if (!active) return
    let cancelled = false
    setInitialLoad(true)
      ; (async () => {
        try {
          const storedId =
            userId != null && practiceId != null ? readLastConversationId(userId, practiceId) : null
          if (storedId != null) {
            const result = await loadConversation(storedId)
            if (result.notFound && userId != null && practiceId != null) {
              clearLastConversationId(userId, practiceId)
              await loadConversation()
            }
          } else {
            await loadConversation()
          }
        } finally {
          if (!cancelled) setInitialLoad(false)
        }
      })()
    return () => {
      cancelled = true
    }
  }, [active, loadConversation, practiceId, userId])

  useEffect(() => {
    if (!active) return
    const el = scrollRef.current
    if (!el) return
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" })
  }, [active, messages, loading])

  useEffect(() => {
    if (active && !initialLoad) {
      textareaRef.current?.focus()
    }
  }, [active, initialLoad])

  useEffect(() => {
    if (!active || initialLoad || initialPrompt == null) return
    setDraft(initialPrompt)
    onInitialPromptApplied?.()
    textareaRef.current?.focus()
  }, [active, initialLoad, initialPrompt, onInitialPromptApplied])

  useEffect(() => {
    if (!active || initialIntakeFormId == null) return
    intakeFormIdForNextSendRef.current = initialIntakeFormId
    onInitialIntakeFormContextApplied?.()
  }, [active, initialIntakeFormId, onInitialIntakeFormContextApplied])

  useEffect(() => {
    if (!attachMenuOpen) return
    const onPointerDown = (event: MouseEvent) => {
      if (!attachMenuRef.current?.contains(event.target as Node)) {
        setAttachMenuOpen(false)
      }
    }
    document.addEventListener("mousedown", onPointerDown)
    return () => document.removeEventListener("mousedown", onPointerDown)
  }, [attachMenuOpen])

  const submitMessage = useCallback(
    async (rawText: string) => {
      const text = rawText.trim()
      if (!text || !text.trim() || loading) return

      const optimisticId = `optimistic-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
      setDraft("")
      setMessages((prev) => [
        ...prev,
        {
          role: "user",
          content: text,
          id: optimisticId,
          attachments: pendingAttachments,
          websiteReference: pendingWebsiteReference ? { url: pendingWebsiteReference } : null
        }
      ])
      setLoading(true)
      clearError()

      const rollbackOptimistic = () => {
        setMessages((prev) => prev.filter((m) => m.id !== optimisticId))
      }

      try {
        const res = await fetch(chatUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-CSRF-Token": readCsrfToken()
          },
          body: JSON.stringify({
            conversation_id: conversationId,
            content: text,
            attachments: pendingAttachments.map(({ contentType, ...attachment }) => ({
              ...attachment,
              ...(contentType ? { content_type: contentType } : {})
            })),
            website_url: pendingWebsiteReference,
            ...(intakeFormIdForNextSendRef.current != null
              ? { intake_form_id: intakeFormIdForNextSendRef.current }
              : {})
          })
        })
        let data: Record<string, unknown> = {}
        try {
          data = (await res.json()) as Record<string, unknown>
        } catch {
          data = {}
        }

        if (!res.ok) {
          const parsed = parseAssistantErrorBody(data, res.status)
          if (parsed.code === "turn_in_progress" && data.turn_id != null) {
            rollbackOptimistic()
            setDraft(text)
            if (data.conversation_id != null) {
              setConversationId(data.conversation_id as number)
            }
            let resumeDispatchedSurfaces = new Set<string>()
            const pollResult = await pollChatTurnStatus(chatStatusUrl, data.turn_id as number, {
              onExtendedWait: () => {
                setError(
                  "The assistant is still working on your request.",
                  "This can take a few minutes for longer tasks. Please wait before sending another message.",
                  "turn_in_progress"
                )
              },
              onPoll: (pollData) => {
                dispatchPortalAiRefreshSurfaces(pollData.refresh_surfaces, resumeDispatchedSurfaces, {
                  refreshBuilderPreview: pollData.refresh_builder_preview
                })
              }
            })
            const reloadedConversationId =
              pollResult.data.conversation_id != null
                ? (pollResult.data.conversation_id as number)
                : (data.conversation_id as number | undefined) ?? conversationId
            if (reloadedConversationId != null) {
              await loadConversation(reloadedConversationId)
            }
            if (pollResult.ok) {
              clearError()
              setPendingAttachments([])
              setPendingWebsiteReference(null)
              intakeFormIdForNextSendRef.current = null
              return
            }
            const resumeParsed = parseAssistantErrorBody(pollResult.data, pollResult.httpStatus)
            setError(resumeParsed.message, resumeParsed.detail, resumeParsed.code)
            return
          }
          if (parsed.code === "enqueue_failed") {
            if (data.conversation_id != null) {
              setConversationId(data.conversation_id as number)
              await loadConversation(data.conversation_id as number)
            }
            setPendingAttachments([])
            setPendingWebsiteReference(null)
            intakeFormIdForNextSendRef.current = null
          } else {
            rollbackOptimistic()
            setDraft(text)
          }
          setError(parsed.message, parsed.detail, parsed.code)
          return
        }

        if (data.conversation_id != null) {
          setConversationId(data.conversation_id as number)
        }

        let dispatchedSurfaces = new Set<string>()

        if (data.status === "pending" && data.turn_id != null) {
          setPendingAttachments([])
          setPendingWebsiteReference(null)
          intakeFormIdForNextSendRef.current = null

          const pollResult = await pollChatTurnStatus(chatStatusUrl, data.turn_id as number, {
            onExtendedWait: () => {
              setError(
                "The assistant is still working on your request.",
                "This can take a few minutes for longer tasks. Please wait before sending another message."
              )
            },
            onPoll: (pollData) => {
              dispatchPortalAiRefreshSurfaces(pollData.refresh_surfaces, dispatchedSurfaces, {
                refreshBuilderPreview: pollData.refresh_builder_preview
              })
            }
          })
          if (!pollResult.ok) {
            const reloadedConversationId =
              pollResult.data.conversation_id != null
                ? (pollResult.data.conversation_id as number)
                : (data.conversation_id as number | undefined) ?? conversationId
            if (reloadedConversationId != null) {
              await loadConversation(reloadedConversationId)
            }
            const parsed = parseAssistantErrorBody(pollResult.data, pollResult.httpStatus)
            setError(parsed.message, parsed.detail, parsed.code)
            return
          }

          data = pollResult.data as Record<string, unknown>
        }

        if (data.error) {
          rollbackOptimistic()
          setDraft(text)
          const parsed = parseAssistantErrorBody(data, res.status)
          setError(parsed.message, parsed.detail, parsed.code)
          return
        }

        dispatchPortalAiRefreshSurfaces(
          Array.isArray(data.refresh_surfaces) ? (data.refresh_surfaces as string[]) : undefined,
          dispatchedSurfaces,
          { refreshBuilderPreview: data.refresh_builder_preview === true }
        )

        const reloadedConversationId =
          data.conversation_id != null ? (data.conversation_id as number) : conversationId
        clearError()
        await loadConversation(reloadedConversationId)
        setPendingAttachments([])
        setPendingWebsiteReference(null)
        intakeFormIdForNextSendRef.current = null
      } catch {
        rollbackOptimistic()
        setDraft(text)
        setError("Network error", "Could not reach the server. Check your connection and try again.")
      } finally {
        setLoading(false)
      }
    },
    [chatStatusUrl, chatUrl, clearError, conversationId, loadConversation, loading, pendingAttachments, pendingWebsiteReference, setError]
  )

  const sendDraft = useCallback(() => {
    void submitMessage(draft)
  }, [draft, submitMessage])

  const startNewChat = useCallback(async () => {
    if (loading) return
    clearError()
    setHistoryOpen(false)
    setLoading(true)
    try {
      const res = await fetch(conversationsUrl, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": readCsrfToken()
        }
      })
      if (!res.ok) {
        let data: Record<string, unknown> = {}
        try {
          data = (await res.json()) as Record<string, unknown>
        } catch {
          data = {}
        }
        const parsed = parseAssistantErrorBody(data, res.status)
        setError(parsed.message, parsed.detail, parsed.code)
        return
      }
      const data = (await res.json()) as { conversationId: number }
      setConversationId(data.conversationId)
      persistLastConversation(data.conversationId)
      setMessages([])
      textareaRef.current?.focus()
    } catch {
      setError("Network error", "Could not reach the server.")
    } finally {
      setLoading(false)
    }
  }, [clearError, conversationsUrl, loading, persistLastConversation, setError])

  const handleConversationDeleted = useCallback(
    async (deletedId: number) => {
      if (conversationId !== deletedId) return
      if (userId != null && practiceId != null) {
        clearLastConversationId(userId, practiceId)
      }
      clearError()
      setDraft("")
      setPendingAttachments([])
      setPendingWebsiteReference(null)
      await loadConversation()
    },
    [clearError, conversationId, loadConversation, practiceId, userId]
  )

  const selectConversation = useCallback(
    async (id: number) => {
      clearError()
      setDraft("")
      setPendingAttachments([])
      setPendingWebsiteReference(null)
      await loadConversation(id)
    },
    [clearError, loadConversation]
  )

  const practiceLabel = practiceName?.trim() || "your practice"

  const handleImagePickerSelect = useCallback(
    (image: { handle?: string; provider?: string; imageUrl?: string; alt?: string }) => {
      const handle = image?.handle
      const provider = image?.provider || "filestack"
      const imageUrl =
        image?.imageUrl || (handle ? `https://cdn.filestackcontent.com/${handle}` : null)

      if (!imageUrl) return

      clearError()
      setPendingAttachments((prev) =>
        [
          ...prev,
          {
            url: imageUrl,
            kind: "image" as const,
            alt: image?.alt || null,
            provider,
            handle: handle || null
          }
        ].slice(0, 5)
      )
    },
    [clearError]
  )

  const handleDocumentPickerSelect = useCallback((document: DocumentPickerSelection) => {
    clearError()
    setPendingAttachments((prev) =>
      [
        ...prev,
        {
          url: document.url,
          kind: "document" as const,
          filename: document.filename,
          contentType: document.contentType,
          provider: document.provider,
          handle: document.handle
        }
      ].slice(0, 5)
    )
  }, [clearError])

  const cardShell =
    "flex h-full min-h-0 w-full flex-col rounded-2xl border border-gray-200/80 bg-white shadow-2xl"

  if (!active) {
    return null
  }

  if (initialLoad) {
    return (
      <div className={cardShell}>
        <div className="flex flex-1 items-center justify-center px-4 py-12">
          <p className="text-sm text-gray-500">Loading Heal.me AI Assistant…</p>
        </div>
      </div>
    )
  }

  return (
    <div className={`${cardShell} relative`}>
      <header className="flex shrink-0 items-center justify-between gap-2 border-b border-gray-100 bg-gray-50/90 px-4 py-3">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-teal-600 text-white shadow">
            <HealmeAiMark
              variant="onTeal"
              logoClassName="h-5 w-auto aspect-[20/29]"
              sparkleClassName="absolute -right-1 -top-0.5 h-3 w-3 text-amber-200 drop-shadow-sm"
            />
          </div>
          <div className="min-w-0">
            <h2 className="truncate text-base font-semibold text-gray-900">Heal.me AI Assistant</h2>
            <p className="truncate text-xs text-gray-500">{practiceLabel}</p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50"
            disabled={loading}
            aria-expanded={historyOpen}
            onClick={() => setHistoryOpen(true)}>
            <span className="inline-flex items-center gap-1">
              <ClockIcon className="size-3.5" aria-hidden />
              History
            </span>
          </button>
          <button
            type="button"
            className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50"
            disabled={loading}
            onClick={() => void startNewChat()}>
            New chat
          </button>
          {onClose ? (
            <button
              type="button"
              className="rounded-lg border border-gray-200 bg-white p-2 text-gray-600 shadow-sm hover:bg-gray-50"
              aria-label="Close assistant"
              onClick={onClose}>
              <XMarkIcon className="h-5 w-5" aria-hidden />
            </button>
          ) : null}
        </div>
      </header>

      <div className="relative flex min-h-0 flex-1 flex-col">
        <div
          ref={scrollRef}
          className="min-h-0 flex-1 overflow-y-auto bg-gradient-to-b from-gray-50/50 to-white px-3 py-4 sm:px-4">
          {messages.length === 0 && !loading ? (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Ask about scheduling, your Heal.me Builder site, services, and publishing — using the same data as you see
                in your Heal.me dashboard.
              </p>
              <details className="group rounded-xl border border-gray-200 bg-white shadow-sm">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-3 py-2.5 text-sm font-medium text-gray-900 marker:hidden [&::-webkit-details-marker]:hidden">
                  <span>Suggested prompts</span>
                  <ChevronDownIcon
                    className="h-5 w-5 shrink-0 text-gray-500 transition-transform duration-200 group-open:rotate-180"
                    aria-hidden
                  />
                </summary>
                <div className="border-t border-gray-100 px-3 pb-3 pt-2">
                  <div className="flex flex-wrap gap-2">
                    {SUGGESTED_PROMPTS.map((label, i) => (
                      <button
                        key={`${i}-${label.slice(0, 48)}`}
                        type="button"
                        disabled={loading}
                        className="max-w-full rounded-full border border-gray-300 bg-gray-50/80 px-3 py-1.5 text-left text-xs font-medium text-gray-800 shadow-sm transition hover:border-teal-600 hover:bg-teal-50 disabled:opacity-50"
                        onClick={() => void submitMessage(label)}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </details>
            </div>
          ) : (
            <ul className="space-y-4" aria-live="polite">
              {messages.map((m) => (
                <li
                  key={
                    typeof m.id !== "undefined"
                      ? String(m.id)
                      : `${m.role}-${m.content.slice(0, 32)}-${m.createdAt ?? ""}`
                  }
                  className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={
                      m.role === "user"
                        ? "max-w-[88%] rounded-2xl rounded-br-md bg-teal-600 px-3.5 py-2 text-left shadow-sm"
                        : "max-w-[88%] rounded-2xl rounded-bl-md border border-gray-100 bg-white px-3.5 py-2 text-gray-900 shadow-sm"
                    }>
                    <PortalAiAssistantChatMarkdown
                      content={m.content}
                      variant={m.role === "user" ? "user" : "assistant"}
                    />
                    {m.attachments && m.attachments.length > 0 ? (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {m.attachments.map((attachment, idx) => (
                          <ChatAttachmentPreview
                            key={`${attachment.url}-${idx}`}
                            attachment={attachment}
                            variant={m.role === "user" ? "user" : "assistant"}
                          />
                        ))}
                      </div>
                    ) : null}
                    {m.websiteReference?.url ? (
                      <div className={`mt-2 text-[11px] ${m.role === "user" ? "text-teal-100" : "text-gray-500"}`}>
                        Website reference:{" "}
                        <a href={m.websiteReference.url} target="_blank" rel="noreferrer" className="underline">
                          {m.websiteReference.url}
                        </a>
                      </div>
                    ) : null}
                    {m.createdAt ? (
                      <p className={`mt-1 text-[10px] ${m.role === "user" ? "text-white/75" : "text-gray-400"}`}>
                        {formatShortTime(m.createdAt)}
                      </p>
                    ) : null}
                  </div>
                </li>
              ))}
              {loading ? (
                <li className="flex justify-start" aria-busy="true">
                  <span className="inline-flex items-center gap-2 rounded-2xl rounded-bl-md border border-gray-100 bg-white px-3.5 py-2 text-sm text-gray-500 shadow-sm">
                    <span className="relative inline-block h-4 w-4 shrink-0" aria-hidden>
                      {WORKING_DOT_MOTION.map((motion, i) => (
                        <span
                          key={i}
                          className="absolute left-1/2 top-1/2 -ml-[2px] -mt-[2px] h-1 w-1 animate-portal-ai-dot-spin rounded-full bg-teal-500 motion-reduce:animate-none"
                          style={motion}
                        />
                      ))}
                    </span>
                    Working…
                  </span>
                </li>
              ) : null}
            </ul>
          )}
        </div>

        <PortalAiAssistantHistoryDrawer
          open={historyOpen}
          conversationsUrl={conversationsUrl}
          activeConversationId={conversationId}
          disabled={loading}
          onClose={() => setHistoryOpen(false)}
          onSelect={selectConversation}
          onDeleted={handleConversationDeleted}
        />
      </div>

      {chatError ? (
        <PortalAiAssistantErrorBanner
          error={chatError}
          showDetails={showErrorDetails}
          onToggleDetails={() => setShowErrorDetails((open) => !open)}
        />
      ) : null}

      <footer className="relative z-10 shrink-0 overflow-visible border-t border-gray-100 bg-white p-3 sm:p-4">
        <div className="relative mb-2 flex flex-wrap items-center gap-2" ref={attachMenuRef}>
          <div className="relative">
            <button
              type="button"
              disabled={loading || pendingAttachments.length >= 5}
              className="rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50"
              aria-expanded={attachMenuOpen}
              aria-haspopup="menu"
              onClick={() => setAttachMenuOpen((open) => !open)}>
              Attach
            </button>
            {attachMenuOpen ? (
              <div
                role="menu"
                className="absolute bottom-full left-0 z-50 mb-1.5 flex w-max min-w-[11rem] flex-col gap-1 rounded-lg border border-gray-200 bg-white p-1.5 shadow-lg">
                <button
                  type="button"
                  role="menuitem"
                  className="block w-full whitespace-nowrap rounded-md px-3 py-2.5 text-left text-xs leading-normal text-gray-700 hover:bg-gray-50 mb-0"
                  onClick={() => {
                    setAttachMenuOpen(false)
                    imagePickerTriggerRef.current?.click()
                  }}>
                  Photo
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="block w-full whitespace-nowrap rounded-md px-3 py-2.5 text-left text-xs leading-normal text-gray-700 hover:bg-gray-50"
                  onClick={() => {
                    setAttachMenuOpen(false)
                    documentPickerTriggerRef.current?.click()
                  }}>
                  Document (PDF/DOCX)
                </button>
              </div>
            ) : null}
          </div>
          <div className="sr-only">
            <ImagePickerV2
              practice={practiceData || {}}
              onImageSelect={handleImagePickerSelect}
              fetching={loading}
              showCurateTab={true}
              trigger={
                <button ref={imagePickerTriggerRef} type="button" tabIndex={-1} aria-hidden>
                  Photo picker
                </button>
              }
            />
            <DocumentPickerV2
              uploadedDocuments={uploadedDocuments}
              onUploadedDocumentsChange={setUploadedDocuments}
              onDocumentSelect={handleDocumentPickerSelect}
              fetching={loading}
              uploadError={documentPickerError}
              onUploadError={setDocumentPickerError}
              trigger={
                <button ref={documentPickerTriggerRef} type="button" tabIndex={-1} aria-hidden>
                  Document picker
                </button>
              }
            />
          </div>
        </div>
        <div className="flex gap-2">
          <textarea
            ref={textareaRef}
            className="max-h-32 min-h-[44px] flex-1 resize-y rounded-xl border border-gray-200 bg-gray-50/80 px-3 py-2.5 text-sm shadow-inner placeholder:text-gray-400 focus:border-teal-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500/20"
            placeholder="Write a message…"
            rows={2}
            value={draft}
            disabled={loading}
            aria-label="Message to Heal.me AI Assistant"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault()
                sendDraft()
              }
            }}
          />
          <button
            type="button"
            className="self-end rounded-xl bg-teal-600 px-4 py-2.5 text-sm font-semibold text-white shadow hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={loading || (!draft.trim() && pendingAttachments.length === 0)}
            onClick={sendDraft}>
            Send
          </button>
        </div>
        {pendingAttachments.length > 0 ? (
          <div className="mt-2 flex flex-wrap gap-2">
            {pendingAttachments.map((attachment, i) => (
              <div key={`${attachment.url}-${i}`} className="relative">
                <ChatAttachmentPreview attachment={attachment} variant="pending" />
                <button
                  type="button"
                  className="absolute -right-1 -top-1 rounded-full bg-white px-1 text-[10px] shadow"
                  onClick={() => setPendingAttachments((prev) => prev.filter((_, idx) => idx !== i))}
                  aria-label="Remove attachment">
                  x
                </button>
              </div>
            ))}
          </div>
        ) : null}
        {pendingWebsiteReference ? (
          <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-teal-50 px-2.5 py-1 text-[11px] text-teal-700">
            <span className="max-w-[18rem] truncate">{pendingWebsiteReference}</span>
            <button
              type="button"
              className="text-teal-700"
              aria-label="Remove website reference"
              onClick={() => setPendingWebsiteReference(null)}>
              x
            </button>
          </div>
        ) : null}
        <p className="mt-2 text-[10px] leading-relaxed text-gray-400">
          Powered by Claude on Heal.me servers. Only your practice&apos;s data is used for answers and actions. Gen AI
          can make mistakes.
        </p>
      </footer>
    </div>
  )
}
